import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Reveal from "@/components/site/Reveal";
import { useT, useRoute } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Newsletter() {
  const t = useT();
  const route = useRoute();
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [consentNews, setConsentNews] = useState(true);
  const [consentData, setConsentData] = useState(true);
  const [loading, setLoading] = useState(false);

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error(t.newsletter.errorRequired);
      return;
    }
    if (!consentNews || !consentData) {
      toast.error(t.newsletter.errorConsent);
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, form);
      toast.success(t.newsletter.success);
      setForm({ name: "", email: "", phone: "" });
    } catch (err) {
      const msg = err?.response?.data?.detail || t.newsletter.errorGeneric;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="bg-black text-white py-24 md:py-32" data-testid="newsletter-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-14 grid grid-cols-1 lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.4em] text-[#C20E1A] font-bold">
            {t.newsletter.eyebrow}
          </p>
          <h2 className="heading-display mt-4 text-5xl sm:text-6xl uppercase">
            {t.newsletter.title1} <span className="text-[#C20E1A]">{t.newsletter.title2}</span>
          </h2>
          <p className="mt-6 text-base text-white/70 leading-relaxed max-w-lg">
            {t.newsletter.body}
          </p>
          <p className="mt-4 text-base text-[#C20E1A] italic">{t.newsletter.tagline}</p>
        </Reveal>

        <Reveal delay={120}>
          <form onSubmit={submit} className="space-y-2" data-testid="newsletter-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
              <input
                data-testid="newsletter-name"
                value={form.name}
                onChange={handle("name")}
                placeholder={t.newsletter.name}
                className="w-full bg-transparent border-b border-white/20 py-4 text-base placeholder:text-white/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none"
              />
              <input
                data-testid="newsletter-email"
                type="email"
                value={form.email}
                onChange={handle("email")}
                placeholder={t.newsletter.email}
                className="w-full bg-transparent border-b border-white/20 py-4 text-base placeholder:text-white/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none"
              />
            </div>
            <input
              data-testid="newsletter-phone"
              value={form.phone}
              onChange={handle("phone")}
              placeholder={t.newsletter.phone}
              className="w-full bg-transparent border-b border-white/20 py-4 text-base placeholder:text-white/40 focus:outline-none focus:border-[#C20E1A] transition-colors rounded-none"
            />

            <div className="pt-6 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="newsletter-consent-news-label">
                <input
                  type="checkbox"
                  data-testid="newsletter-consent-news"
                  checked={consentNews}
                  onChange={(e) => setConsentNews(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#C20E1A] cursor-pointer"
                />
                <span className="text-xs text-white/70 leading-relaxed group-hover:text-white/90 transition-colors">
                  {t.newsletter.consentNewsletter}
                </span>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="newsletter-consent-data-label">
                <input
                  type="checkbox"
                  data-testid="newsletter-consent-data"
                  checked={consentData}
                  onChange={(e) => setConsentData(e.target.checked)}
                  className="mt-1 h-4 w-4 shrink-0 accent-[#C20E1A] cursor-pointer"
                />
                <span className="text-xs text-white/70 leading-relaxed group-hover:text-white/90 transition-colors">
                  {t.newsletter.consentDataProcessing}{" "}
                  <Link
                    to={route("privacy")}
                    className="text-[#C20E1A] underline underline-offset-2 hover:text-white"
                    data-testid="newsletter-consent-privacy-link"
                  >
                    {t.newsletter.consentPrivacyLink}
                  </Link>
                </span>
              </label>
            </div>

            <button
              data-testid="newsletter-submit"
              type="submit"
              disabled={loading}
              className="mt-8 bg-[#C20E1A] text-white px-9 py-4 text-[11px] uppercase tracking-[0.25em] font-bold hover:bg-white hover:text-black transition-colors duration-500 disabled:opacity-60"
            >
              {loading ? t.newsletter.submitting : t.newsletter.cta}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
